import React, { useMemo, useState } from 'react';
import { Todo, TodoNavigationTagCount, TodoTagEntity } from '../types/data';
import Icon from './icons/Icon';
import './TodoMenuTagList.css';

interface TodoMenuTagListProps {
  tags: TodoTagEntity[];
  todos: Todo[];
  onTagSelect?: (tagId: string | null) => void;
  selectedTagId?: string | null;
  tagCounts?: TodoNavigationTagCount[];
  /** 表示用の絞り込み文字列（未分類は常に表示） */
  filterText?: string;
  onReorderTags?: (sourceId: string, targetId: string, insertAfter: boolean) => void | Promise<void>;
}

const TodoMenuTagList: React.FC<TodoMenuTagListProps> = ({
  tags,
  todos,
  onTagSelect,
  selectedTagId,
  tagCounts,
  filterText = '',
  onReorderTags
}) => {
  const [draggingTagId, setDraggingTagId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<{ id: string; insertAfter: boolean } | null>(null);

  // 絞り込み済みタグ
  const filteredTags = useMemo(() => {
    const q = (filterText || '').trim().toLowerCase();
    if (!q) return tags;
    return tags.filter(t => (t.title || '').toLowerCase().includes(q));
  }, [tags, filterText]);

  const canReorder = Boolean(onReorderTags) && (filterText || '').trim() === '';
  const tagCountById = useMemo(() => {
    const map = new Map<string | null, number>();
    tagCounts?.forEach(item => map.set(item.tag_id, item.open_count));
    return map;
  }, [tagCounts]);

  // カウント数を計算
  const getTagCount = useMemo(() => {
    return (tagId: string | null): number => {
      try {
        if (tagCounts && tagCountById.has(tagId)) {
          return tagCountById.get(tagId) ?? 0;
        }
        if (tagId === null) {
          // 「未分類」のカウント
          return todos.filter(todo =>
            !todo.deleted && !todo.done && !todo.canceled &&
            (!todo.todoTagIds || todo.todoTagIds.length === 0)
          ).length;
        } else {
          // 各タグのカウント
          return todos.filter(todo =>
            !todo.deleted && !todo.done && !todo.canceled &&
            todo.todoTagIds?.includes(tagId)
          ).length;
        }
      } catch (error) {
        console.error('Failed to calculate tag count:', error);
        return 0;
      }
    };
  }, [todos, tagCounts, tagCountById]);

  const handleTagClick = (tagId: string | null) => {
    onTagSelect?.(tagId);
  };

  return (
    <div className="todo-tag-list">
      {/* 未分類 */}
      <div
        className={`todo-tag-item ${selectedTagId === null ? 'selected' : ''}`}
        onClick={() => handleTagClick(null)}
      >
        <div className='todo-tag-item-state-layer'>
          <div className="todo-tag-item-content">
            <div className="todo-tag-item-left">
              <Icon name="empty" size={24} />
              <span className="todo-tag-title text-single-large">未分類</span>
            </div>
            {(() => {
              const count = getTagCount(null);
              return count > 0 ? (
                <span className="todo-tag-count text-single-large">{count}</span>
              ) : null;
            })()}
          </div>
        </div>
      </div>

      {/* タグ一覧（絞り込み適用） */}
      {filteredTags.map((tag) => (
        <div
          key={tag.id}
          className={[
            'todo-tag-item',
            selectedTagId === tag.id ? 'selected' : '',
            draggingTagId === tag.id ? 'dragging' : '',
            dragOver?.id === tag.id ? (dragOver.insertAfter ? 'drag-over-after' : 'drag-over-before') : '',
          ].filter(Boolean).join(' ')}
          onClick={() => handleTagClick(tag.id)}
          role="button"
          draggable={canReorder}
          aria-grabbed={draggingTagId === tag.id}
          onDragStart={(e) => {
            if (!canReorder) return;
            e.dataTransfer.effectAllowed = 'move';
            setDraggingTagId(tag.id);
          }}
          onDragEnd={() => {
            setDraggingTagId(null);
            setDragOver(null);
          }}
          onDragOver={(e) => {
            if (!canReorder) return;
            if (!draggingTagId) return;
            if (draggingTagId === tag.id) return;
            e.preventDefault();
            const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            const insertAfter = (e.clientY - rect.top) > rect.height / 2;
            setDragOver({ id: tag.id, insertAfter });
          }}
          onDragLeave={() => {
            if (dragOver?.id === tag.id) setDragOver(null);
          }}
          onDrop={async (e) => {
            if (!canReorder) return;
            if (!draggingTagId) return;
            if (draggingTagId === tag.id) return;
            e.preventDefault();
            const insertAfter = dragOver?.id === tag.id ? dragOver.insertAfter : false;
            setDragOver(null);
            try {
              await Promise.resolve(onReorderTags?.(draggingTagId, tag.id, insertAfter));
            } finally {
              setDraggingTagId(null);
            }
          }}
        >
          <div className='todo-tag-item-state-layer'>
            <div className="todo-tag-item-content">
              <div className="todo-tag-item-left">
                <Icon name="tag" size={24} />
                <span className="todo-tag-title text-single-large">{tag.title}</span>
              </div>
              {(() => {
                const count = getTagCount(tag.id);
                return count > 0 ? (
                  <span className="todo-tag-count text-single-large">{count}</span>
                ) : null;
              })()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TodoMenuTagList;
